import React from 'react';

import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
  ScriptableContext,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useThemeContext } from '@infoods/lifebase-ui';

import classes from './ScanAnalyticsGraph.module.scss';
import { ScanAnalyticsGraphData } from '../../../../interfaces/entity-dashboard/ScanAnalyticsData';
import { getGraphInfo } from './utils/getGraphInfo/getGraphInfo';
import {
  getShowFirstAndLastLabelsParser,
  getTooltipBaseOptions,
} from '../../../../../shared/features/charts';
import { getGraphPlugins } from './ScanAnalyticsGraph.settings';

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
);

type ScanAnalyticsGraphProps = {
  scanAnalyticsGraphData: ScanAnalyticsGraphData;
};

const ScanAnalyticsGraph = ({ scanAnalyticsGraphData }: ScanAnalyticsGraphProps) => {
  const { palette } = useThemeContext();

  const { labels, newScansValues } = getGraphInfo(scanAnalyticsGraphData);

  const fontCaption12Medium = {
    family: "'Lato', sans-serif",
    size: 12,
    weight: 500,
  } as const;

  const ticksOptions = {
    color: palette.neutral[600],
    font: fontCaption12Medium,
    maxRotation: 0,
  };

  const graphPlugins = getGraphPlugins(palette);
  const tooltipBaseOptions = getTooltipBaseOptions(palette);

  return (
    <div className={classes.graph}>
      <Line
        data={{
          labels,
          datasets: [
            {
              data: newScansValues,
              label: 'Scans This Period',
              borderColor: palette.info[400],
              borderWidth: 1.2,
              fill: true,
              backgroundColor: (context: ScriptableContext<'line'>) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;

                if (!chartArea) {
                  return 'transparent';
                }

                const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom,
                );
                gradient.addColorStop(0, 'rgba(40, 108, 217, 0.45)');
                gradient.addColorStop(0.45, 'rgba(94, 134, 223, 0.3)');
                gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

                return gradient;
              },
            },
          ],
        }}
        options={{
          animation: false,
          maintainAspectRatio: false,
          elements: {
            point: {
              radius: 0,
              hoverRadius: 0,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              ticks: {
                ...ticksOptions,
                maxRotation: 0,
                autoSkip: false,
                align: 'inner',
                callback: getShowFirstAndLastLabelsParser(
                  (value) => labels[value as number],
                ),
                padding: 0,
              },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
            },
            y: {
              position: 'right',
              beginAtZero: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
              ticks: {
                ...ticksOptions,
                maxTicksLimit: 5,
                callback: (value) => String(value),
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipBaseOptions,
            },
          },
        }}
        plugins={graphPlugins}
      />
    </div>
  );
};

export { ScanAnalyticsGraph };
